/**
 * useProAccess Hook
 *
 * Simple hook to check if user has Pro access
 * Includes beta tester support
 *
 * Usage:
 *   const { isPro, isBeta, isLoading, trialInfo } = useProAccess();
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *   if (!isPro) {
 *     return <RevenueCatPaywall />;
 *   }
 *
 * IMPORTANT: isLoading is true while:
 *   1. Auth is still loading (user === null && authLoading === true)
 *   2. Pro access check is in progress
 *
 * isLoading becomes false when:
 *   1. User is logged out (user === null && authLoading === false) -> isPro = false
 *   2. Pro access check completes -> isPro = true/false based on result
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getLapsedEntitlementInfo } from '../revenuecat';
import { fetchEntitlementStatus } from '../entitlementStatus';
import { checkBetaTesterStatus, type BetaTesterInfo } from '../betaTesters';
import { useAuth } from '../AuthContext';

/**
 * Information about a recently-lapsed trial / subscription. Used to
 * render a win-back banner inviting the user to come back. We treat
 * lapsed-within-30-days as actionable; older than that the urgency is
 * gone and we should rely on email re-engagement instead.
 */
export interface LapsedTrialInfo {
  hadEntitlement: boolean;
  daysSinceExpired: number | null;
  subscriptionType: 'monthly' | 'yearly' | 'none';
}

export interface TrialInfo {
  isInTrial: boolean;
  daysRemaining: number | null;
  isExpiringSoon: boolean; // Less than 3 days
  /** True when daysRemaining <= 1. Drives the most urgent banner tier. */
  isExpiringTomorrow: boolean;
  expirationDate: string | null;
  /**
   * Which plan the user trialed into. Used by TrialBanner to show
   * plan-specific subscribe pricing instead of a hardcoded fallback.
   * 'none' when no entitlement is active.
   */
  subscriptionType: 'monthly' | 'yearly' | 'none';
  /**
   * WHICH trial this is. The two are completely different products and
   * need different copy:
   *   'store'  — a real App Store / Play free trial. The user has already
   *              committed a payment method and it AUTO-CONVERTS to paid.
   *              Telling them to "Subscribe Now" is wrong and sends them to
   *              a paywall where their current plan is (correctly) not
   *              selectable — which reads as a broken app.
   *   'opmode' — the app-side 14-day no-card trial (users/{uid}.trial).
   *              Here "Subscribe Now" is exactly right; nothing auto-converts.
   */
  source: 'store' | 'opmode' | 'none';
  /**
   * Hours left, for sub-day accuracy. daysRemaining uses Math.ceil, so a
   * trial ending in 6 hours reports 1 day and the banner said "ends
   * tomorrow" when it ends today.
   */
  hoursRemaining: number | null;
}

// How many program workouts a brand-new user can complete before the
// paywall appears. The point is to let users feel the AI/gamification
// loop once before being asked for a card; one workout is enough.
const FREE_PREVIEW_WORKOUTS = 1;

// OPMODE-controlled 14-day no-card trial — auto-granted on first
// useProAccess check. Lives in Firestore users/{uid}.trial so it can't
// be reset by reinstalling the app. RevenueCat is not involved: the
// paywall in RC is configured WITHOUT a free-trial offer so users hit a
// clean "subscribe or stop" wall after the 14 days. RC entitlement
// (when present) still takes precedence — a paying subscriber doesn't
// need the trial countdown.
const OPMODE_TRIAL_DAYS = 14;

export function useProAccess() {
  const { user, loading: authLoading } = useAuth();
  const [isPro, setIsPro] = useState(false);
  const [isBeta, setIsBeta] = useState(false);
  const [betaInfo, setBetaInfo] = useState<BetaTesterInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Free-preview state. `hasPreviewAccess` is true when the user has
  // workouts remaining in their preview allowance AND the global
  // featureFlags/freePreviewEnabled flag is true. Non-Pro users get
  // through the paywall while it's true.
  const [hasPreviewAccess, setHasPreviewAccess] = useState(false);
  const [previewWorkoutsRemaining, setPreviewWorkoutsRemaining] = useState<number | null>(null);
  // Lapsed-trial state for win-back UX. Populated only for users without
  // active Pro access who have a historical RC entitlement that recently
  // expired.
  const [lapsedTrialInfo, setLapsedTrialInfo] = useState<LapsedTrialInfo>({
    hadEntitlement: false,
    daysSinceExpired: null,
    subscriptionType: 'none',
  });
  const [trialInfo, setTrialInfo] = useState<TrialInfo>({
    isInTrial: false,
    daysRemaining: null,
    isExpiringSoon: false,
    isExpiringTomorrow: false,
    expirationDate: null,
    subscriptionType: 'none',
    source: 'none',
    hoursRemaining: null,
  });

  // Track if we've completed the initial check to prevent race conditions
  const hasCheckedRef = useRef(false);
  const checkInProgressRef = useRef(false);

  useEffect(() => {
    // If auth is still loading, keep our loading state true
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    // Auth finished loading
    if (!user) {
      // User is logged out - no pro access, not loading
      setIsPro(false);
      setIsBeta(false);
      setBetaInfo(null);
      setIsLoading(false);
      hasCheckedRef.current = true;
      return;
    }

    // User is logged in - check access
    // Prevent duplicate checks
    if (!checkInProgressRef.current) {
      checkAccess();
    }
  }, [user, authLoading]);

  const checkAccess = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    // Prevent concurrent checks
    if (checkInProgressRef.current) {
      return;
    }
    checkInProgressRef.current = true;
    setIsLoading(true);

    try {
      // FIRST: Admin bypass + harvest the free-preview counter from the
      // same user doc read. Admin/owner accounts get permanent Pro
      // access regardless of beta or RevenueCat. Non-admins fall
      // through with their `freeWorkoutsUsed` already in scope so we
      // don't need a second Firestore round-trip later.
      let freeWorkoutsUsed = 0;
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          freeWorkoutsUsed = Number(data?.freeWorkoutsUsed) || 0;
          if (data?.role === 'admin') {
            console.log('ADMIN ACCESS GRANTED — Pro bypass active');
            setIsBeta(false);
            setIsPro(true);
            setHasPreviewAccess(false); // admins don't need preview
            setPreviewWorkoutsRemaining(null);
            setTrialInfo({ isInTrial: false, daysRemaining: null, isExpiringSoon: false, isExpiringTomorrow: false, expirationDate: null, subscriptionType: 'none', source: 'none', hoursRemaining: null });
            setIsLoading(false);
            checkInProgressRef.current = false;
            hasCheckedRef.current = true;
            return;
          }
        }
      } catch (adminError) {
        console.warn('Admin check failed, continuing to beta/RevenueCat:', adminError);
      }

      // Read the global feature flag so the kill switch works without a
      // redeploy. Default to enabled if the doc doesn't exist yet.
      let freePreviewEnabled = true;
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const flagSnap = await getDoc(doc(db, 'appConfig', 'featureFlags'));
        if (flagSnap.exists() && flagSnap.data()?.freePreviewEnabled === false) {
          freePreviewEnabled = false;
        }
      } catch {
        // Non-fatal — default to enabled.
      }

      const remaining = Math.max(0, FREE_PREVIEW_WORKOUTS - freeWorkoutsUsed);
      setPreviewWorkoutsRemaining(remaining);
      setHasPreviewAccess(freePreviewEnabled && remaining > 0);

      // SECOND: Check beta tester status (isolated try/catch — must not fail into RevenueCat)
      try {
        const { isBetaPeriodActive, getDaysRemainingInBeta } = await import('../betaTesters');
        const betaStatus = await checkBetaTesterStatus(user.uid);
        setBetaInfo(betaStatus);

        const betaPeriodActive = isBetaPeriodActive();
        const isFlaggedBetaTester = betaStatus.isBetaTester === true;

        if (betaPeriodActive && isFlaggedBetaTester) {
          const daysRemaining = await getDaysRemainingInBeta();
          console.log(`BETA TESTER ACCESS GRANTED - ${daysRemaining} days remaining`);
          setIsBeta(true);
          setIsPro(true);
          setTrialInfo({
            isInTrial: false,
            daysRemaining: daysRemaining,
            isExpiringSoon: daysRemaining <= 7,
            isExpiringTomorrow: daysRemaining <= 1,
            expirationDate: null,
            subscriptionType: 'none',
            source: 'none',
            hoursRemaining: null,
          });
          setIsLoading(false);
          checkInProgressRef.current = false;
          hasCheckedRef.current = true;
          return; // EXIT — beta granted, skip RevenueCat entirely
        }
      } catch (betaError) {
        console.warn('Beta check failed, continuing to RevenueCat:', betaError);
      }

      // Not a beta tester (or check failed) — try RevenueCat
      setIsBeta(false);

      // THIRD: Check RevenueCat for regular subscription/trial.
      //
      // Reads through lib/entitlementStatus, which is platform-aware: the
      // Capacitor SDK on native, REST on web. This replaces hasProAccess() /
      // getSubscriptionDetails() from lib/revenuecat.ts, which read a web-SDK
      // instance that was NEVER initialised anywhere in the app and therefore
      // returned false for every user on every platform — meaning a real
      // paying subscriber was never granted Pro. See lib/entitlementStatus.ts.
      const entitlement = await fetchEntitlementStatus(user.uid);
      const hasAccess = entitlement.isActive;
      setIsPro(hasAccess);

      const details = {
        isInTrial: entitlement.isInTrial,
        expirationDate: entitlement.expirationDate,
        subscriptionType: entitlement.subscriptionType,
      };

      if (details.isInTrial && details.expirationDate) {
        const expirationDate = new Date(details.expirationDate);
        const now = new Date();
        const remainingMs = expirationDate.getTime() - now.getTime();
        const daysRemaining = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
        const actualDaysRemaining = Math.max(0, daysRemaining);
        const hoursRemaining = Math.max(0, Math.ceil(remainingMs / (1000 * 60 * 60)));

        setTrialInfo({
          isInTrial: true,
          daysRemaining: actualDaysRemaining,
          isExpiringSoon: actualDaysRemaining <= 3 && actualDaysRemaining > 0,
          isExpiringTomorrow: actualDaysRemaining <= 1 && actualDaysRemaining > 0,
          expirationDate: details.expirationDate,
          subscriptionType: details.subscriptionType,
          // A real store free trial: payment method already committed, and it
          // auto-converts. The banner must NOT tell these users to subscribe.
          source: 'store',
          hoursRemaining,
        });

        // Schedule local push notifications for 3-day and 1-day warnings
        // so users who haven't opened the app still see the alert. Fire-
        // and-forget — failure here doesn't block the access check.
        import('../notifications')
          .then(({ scheduleTrialExpirationAlerts }) =>
            scheduleTrialExpirationAlerts(details.expirationDate, true),
          )
          .catch(() => { /* non-fatal */ });
      } else {
        setTrialInfo({
          isInTrial: false,
          daysRemaining: null,
          isExpiringSoon: false,
          isExpiringTomorrow: false,
          expirationDate: null,
          subscriptionType: details.subscriptionType,
          source: 'none',
          hoursRemaining: null,
        });

        // Cancel any pending trial-expiration alerts — user has either
        // converted to paid (no longer in trial) or the trial ended.
        // Either way, the alerts shouldn't fire.
        import('../notifications')
          .then(({ scheduleTrialExpirationAlerts }) =>
            scheduleTrialExpirationAlerts(null, false),
          )
          .catch(() => { /* non-fatal */ });
      }

      // Did we actually get an ANSWER from RevenueCat, or just fail to reach
      // it? `source: 'unavailable'` means "could not determine" — NOT a
      // confirmed "no". Treating the two the same would show a paying
      // subscriber the OPMODE trial countdown (wrong number, wrong urgency)
      // or, once that lapsed, the hard paywall — because their SDK read
      // happened to fail. entitlementStatus.ts documents this contract;
      // this is the caller honouring it.
      const entitlementKnown = entitlement.source !== 'unavailable';

      if (!entitlementKnown) {
        // Fail OPEN. We could not reach RevenueCat, so we do not know whether
        // this person is a subscriber. Gating a paying customer because of a
        // network blip is far worse than briefly over-granting during an
        // outage — especially as every user already gets a 14-day trial.
        console.warn(
          '[access] entitlement could not be determined — granting provisional ' +
            'access rather than gating a possible subscriber',
        );
        setIsPro(true);
      }

      // ── OPMODE-controlled 14-day trial ──
      // Runs only when RC didn't already grant Pro AND we actually know that.
      // Reads (or first-grants) users/{uid}.trial.endsAt. While the trial is
      // live, we override isPro=true and populate trialInfo so the existing
      // TrialBanner + gating chain just works. After the trial ends,
      // isPro stays false → the configured RC paywall fires (hard wall).
      if (!hasAccess && entitlementKnown) {
        try {
          const { doc: fsDoc, getDoc: fsGetDoc, updateDoc: fsUpdate, setDoc: fsSetDoc, serverTimestamp } = await import('firebase/firestore');
          const { db: fsDb } = await import('../firebase');
          const userRef = fsDoc(fsDb, 'users', user.uid);
          const snap = await fsGetDoc(userRef);
          let endsAt: Date | null = null;
          if (snap.exists()) {
            const t = snap.data()?.trial;
            if (t?.endsAt) {
              endsAt = typeof t.endsAt?.toDate === 'function' ? t.endsAt.toDate() : new Date(t.endsAt);
            }
          }
          if (!endsAt) {
            // First-time grant. Anyone without a `trial` field gets one
            // — including existing accounts on first access after this
            // ships. That's intentional (one-time gift) and only fires
            // once per uid; can't be farmed by reinstalling.
            const now = new Date();
            endsAt = new Date(now.getTime() + OPMODE_TRIAL_DAYS * 86_400_000);
            const trialDoc = { trial: { grantedAt: serverTimestamp(), endsAt, source: 'auto' as const } };
            try {
              await fsUpdate(userRef, trialDoc as any);
            } catch {
              try { await fsSetDoc(userRef, trialDoc as any, { merge: true }); } catch { /* non-fatal */ }
            }
          }
          const remainingMs = endsAt ? endsAt.getTime() - Date.now() : 0;
          if (endsAt && remainingMs > 0) {
            const daysRemaining = Math.max(0, Math.ceil(remainingMs / 86_400_000));
            setIsPro(true);
            setTrialInfo({
              isInTrial: true,
              daysRemaining,
              isExpiringSoon: daysRemaining <= 3 && daysRemaining > 0,
              isExpiringTomorrow: daysRemaining <= 1 && daysRemaining > 0,
              expirationDate: endsAt.toISOString(),
              subscriptionType: 'none',
              // The app-side no-card trial. Nothing auto-converts, so
              // "Subscribe" is the correct call to action here.
              source: 'opmode',
              hoursRemaining: Math.max(0, Math.ceil(remainingMs / 3_600_000)),
            });
          }
        } catch (e) {
          console.warn('OPMODE trial check failed:', e);
        }
      }

      // Populate lapsed-trial info for win-back banners. Only meaningful
      // when the user is currently NOT entitled — `getLapsedEntitlementInfo`
      // returns a no-op when active entitlement exists. Skipped when the
      // entitlement is merely unknown: telling an active subscriber "your
      // trial lapsed, come back" because the network blipped is worse than
      // showing no banner at all.
      if (!hasAccess && entitlementKnown) {
        try {
          const lapsed = await getLapsedEntitlementInfo();
          setLapsedTrialInfo(lapsed);

          // Persist lapsedAt so the scheduled win-back email function
          // can find the user later. Fire-and-forget — if this write
          // fails the banner still renders, just no email tier kicks in.
          if (lapsed.hadEntitlement && user) {
            try {
              const { doc, getDoc, updateDoc, serverTimestamp } = await import('firebase/firestore');
              const { db } = await import('../firebase');
              const userRef = doc(db, 'users', user.uid);
              const snap = await getDoc(userRef);
              // Only stamp lapsedAt once per lapse cycle. If it's
              // already set, leave it alone (next subscription will
              // clear it via the entitled branch below).
              if (snap.exists() && !snap.data()?.lapsedAt) {
                await updateDoc(userRef, { lapsedAt: serverTimestamp() });
              }
            } catch { /* non-fatal */ }
          }
        } catch {
          // Non-fatal — banner just won't render.
        }
      } else {
        setLapsedTrialInfo({ hadEntitlement: false, daysSinceExpired: null, subscriptionType: 'none' });

        // User is entitled again. Clear lapsedAt and reset the email
        // tier flags so a future lapse can fire fresh.
        if (user) {
          try {
            const { doc, getDoc, updateDoc, deleteField } = await import('firebase/firestore');
            const { db } = await import('../firebase');
            const userRef = doc(db, 'users', user.uid);
            const snap = await getDoc(userRef);
            if (snap.exists() && snap.data()?.lapsedAt) {
              await updateDoc(userRef, {
                lapsedAt: deleteField(),
                winBackEmailsSent: deleteField(),
              });
            }
          } catch { /* non-fatal */ }
        }
      }
    } catch (error) {
      console.error(' Error checking Pro access:', error);
      setIsPro(false);
      setIsBeta(false);
      setBetaInfo(null);
      // Fail-OPEN for the preview: if the whole check errored out (network
      // hiccup, Firestore offline, RevenueCat unreachable) and we never
      // got around to setting previewWorkoutsRemaining, give the user the
      // benefit of the doubt rather than locking them out. The counter
      // still increments server-side on actual session completion, so a
      // user with 1 used preview stays gated on subsequent loads.
      setPreviewWorkoutsRemaining(prev => prev ?? FREE_PREVIEW_WORKOUTS);
      setHasPreviewAccess(prev => prev || true);
      setTrialInfo({
        isInTrial: false,
        daysRemaining: null,
        isExpiringSoon: false,
        isExpiringTomorrow: false,
        expirationDate: null,
        subscriptionType: 'none',
        source: 'none',
        hoursRemaining: null,
      });
    } finally {
      setIsLoading(false);
      checkInProgressRef.current = false;
      hasCheckedRef.current = true;
    }
  }, [user]);

  return {
    isPro: isPro || isBeta, // Beta testers always have Pro access
    isBeta,
    betaInfo,
    isLoading,
    trialInfo,
    /**
     * True when a non-Pro user still has free-preview workouts left AND
     * the global featureFlags/freePreviewEnabled flag is true. Gates
     * that previously checked `!isPro` should now check
     * `!isPro && !hasPreviewAccess` to let preview users through.
     */
    hasPreviewAccess,
    /** Workouts left in the free preview, or null while loading. */
    previewWorkoutsRemaining,
    /**
     * Information about a recently-expired RevenueCat entitlement, used
     * to render a win-back banner. `hadEntitlement` is true when the user
     * has a non-active entitlement in their history; `daysSinceExpired`
     * is computed from its expirationDate.
     */
    lapsedTrialInfo,
    refresh: checkAccess,
  };
}

